import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, formatDate } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyCheckboxChange as MatCheckboxChange, MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyPaginator as MatPaginator, MatLegacyPaginatorModule as MatPaginatorModule, LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MaterialSortProperty } from '@dave/types/dist/proto/erp/material';
import { Status } from '@dave/types/dist/proto/erp/materialList';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TableVirtualScrollDataSource, TableVirtualScrollModule } from 'ng-table-virtual-scroll';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { debounceTime, filter, map, shareReplay, skip, switchMap, tap } from 'rxjs/operators';
import { MaterialListResolver } from '../../../dave-data-module/guards/material-list.resolver';
import { QuantityTypeResolver } from '../../../dave-data-module/guards/quantity-type.resolver';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { MaterialDataService } from '../../../dave-data-module/services/material-data.service';
import { materialBackendSort, PaginatedMaterialService } from '../../../dave-data-module/services/paginated-material.service';
import { State } from '../../../dave-data-module/State';
import { MaterialListActionTypes } from '../../../dave-data-module/State/actions/material-list.actions';
import { getQuantityTypeDictionary, getQuantityTypesFetched } from '../../../dave-data-module/State/selectors/accounting.selector';
import { getMaterialLists, getMaterialListsFetched } from '../../../dave-data-module/State/selectors/material-list.selector';
import { isNotNullOrUndefined, SearchQueriesDebounceTimeBackend, uniqArray } from '../../../helper/helper';
import { LoadingService } from '../../../services/loading.service';
import { SelectMaterialPopupComponentDialogData } from '../select-material-popup/select-material-popup.component';

interface TableData {
    MaterialId: number;
    Name: string;
    Type: string | null;
    Available: number | null;
}
const tableSortToBackendSort = (sort: string): materialBackendSort => {
    switch (sort as keyof TableData) {
        case 'Available':
            return MaterialSortProperty.Amount;
        case 'Name':
            return MaterialSortProperty.Name;
        default:
            return undefined;
    }
};
@Component({
    selector: 'app-select-material',
    standalone: true,
    imports: [
        CommonModule,
        FontAwesomeModule,
        MatChipsModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        MatSortModule,
        MatTableModule,
        ReactiveFormsModule,
        ScrollingModule,
        TableVirtualScrollModule,
        MatPaginatorModule,
        FormsModule,
    ],
    providers: [PaginatedMaterialService],
    templateUrl: './select-material.component.html',
    styleUrls: ['./select-material.component.scss'],
})
export class SelectMaterialComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(CdkVirtualScrollViewport) CdkVirtualScrollViewport: CdkVirtualScrollViewport;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @Input() ShowHeadline = true;
    @Input() ComponentData: SelectMaterialPopupComponentDialogData;

    private readonly _pagedMaterialService = inject(PaginatedMaterialService);
    private readonly _materialService = inject(MaterialDataService);
    length$ = this._pagedMaterialService.selectLength$;
    pageSize$ = this._pagedMaterialService.selectPageSize$;

    public Selected$: Observable<{ id: number; label: string }[]>;
    private searchString: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public DataSource$: BehaviorSubject<TableVirtualScrollDataSource<TableData>> = new BehaviorSubject(new TableVirtualScrollDataSource<TableData>([]));
    public PersonColumnHeaders = {
        Name: 'Name',
        Available: 'Verfügbar',
        Type: 'Einheit',
    };
    public SelectedRowIndex: number;
    public PersonAutoColumns = ['Name', 'Type', 'Available'];
    public Multiselect = new BehaviorSubject(false);
    public Columns$ = this.Multiselect.pipe(map((m) => ['checkbox', 'Name', 'Type', 'Available', 'formControl']));
    private subscriptions: Subscription[] = [];
    constructor(private store: Store<State>, private materialListResolver: MaterialListResolver, private qtResolver: QuantityTypeResolver, protected ls: LoadingService, private actions$: Actions) {}

    public Save() {
        this.ls.startLoading('app-select-material--Save');
        const selected: [number, number][] = [];
        this.selectedMaterials.forEach((value: number, key: number) => {
            if (value) {
                selected.push([key, value]);
            }
        });
        if (selected.length) {
            firstValueFrom(this.actions$.pipe(ofType(MaterialListActionTypes.CreateSuccess, MaterialListActionTypes.CreateFailure), skip(selected.length - 1))).then(() => {
                selected.forEach(([id, value]) => {
                    this.selectedMaterials.set(id, null);
                });
                this.ls.endLoading('app-select-material--Save');
            });
            selected.forEach(([id, value]) => {
                this.store.dispatch(
                    MaterialListActionTypes.CreateRequest({
                        Payload: {
                            MaterialId: id?.toString(),
                            Amount: value,
                            CommissionId: this.ComponentData.CommissionId?.toString(),
                            Status: Status.OPEN,
                        },
                    }),
                );
            });
        } else {
            this.ls.endLoading('app-select-material--Save');
        }
    }

    RemoveSelected(id: number) {
        this.store.dispatch(MaterialListActionTypes.Delete({ Payload: { Id: String(id) } }));
    }

    public DoSearch(value: string | null) {
        return this.searchString.next(value);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.searchString.complete();
    }
    loadFirstPage() {
        this._pagedMaterialService.loadPage({ offset: 0 });
        this.paginator.pageIndex = 0;
    }

    protected selectedMaterials = new Map<number, number>([]);
    ngOnInit(): void {
        this.subscriptions.push(
            this.searchString.pipe(skip(1), debounceTime(SearchQueriesDebounceTimeBackend)).subscribe((query) => {
                this._pagedMaterialService.setSearch(query).then(() => this.loadFirstPage());
            }),
        );
        this.Selected$ = getFetched$(this.store, getMaterialListsFetched, getMaterialLists, this.materialListResolver).pipe(
            switchMap((ml) => {
                const fMl = ml.filter((m) => m.CommissionId === this.ComponentData.CommissionId && !m.DeletedAt);
                return fMl.length
                    ? combineLatest([this._materialService.getMaterialsById$(uniqArray(fMl.map((m) => m.MaterialId)), false), this.store.select(getQuantityTypeDictionary)]).pipe(
                          map(([materials, qTypes]) => {
                              return fMl.map((m) => {
                                  const material = materials.find(x => x.Id === m.MaterialId);
                                  return {
                                      id: m?.Id,
                                      label:
                                          material?.Name +
                                          ' ( ' +
                                          formatDate(m?.CreatedAt, 'shortDate', 'de-DE') +
                                          ' ) ' +
                                          m?.Amount +
                                          ' ' +
                                          (material?.QuantityTypeId ? qTypes[material.QuantityTypeId]?.Name : ''),
                                  };
                              });
                          }),
                      )
                    : of([]);
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
    }

    ngAfterViewInit(): void {
        this._pagedMaterialService.loadPage();
        this.subscriptions.push(
            combineLatest([
                this._pagedMaterialService.selectItems$.pipe(
                    tap(() => {
                        this.scrollTop();
                    }),
                ),
                getFetched$(this.store, getQuantityTypesFetched, getQuantityTypeDictionary, this.qtResolver),
            ])
                .pipe(
                    map(([materials, qTypes]) => {
                        return new TableVirtualScrollDataSource(
                            materials.map<TableData>((material) => {
                                const type = material.QuantityTypeId && qTypes[material.QuantityTypeId]?.Name;
                                return {
                                    MaterialId: material.Id,
                                    Name: material.Name,
                                    Type: type ? type : '',
                                    Available: isNotNullOrUndefined(material?.Amount) ? material.Amount : null,
                                };
                            }),
                        );
                    }),
                )
                .subscribe((v) => {
                    this.DataSource$.next(v);
                }),
        );
    }

    public scrollTop() {
        this.CdkVirtualScrollViewport?.scrollTo({ top: 0 });
    }
    sortChange($event: Sort) {
        this._pagedMaterialService.setSort({ sortDirection: $event.direction || undefined, sortProperty: tableSortToBackendSort($event.active) }).then(() => this.loadFirstPage());
    }
    pageChange($event: PageEvent) {
        this._pagedMaterialService.loadPage({ offset: $event.pageIndex * $event.pageSize });
    }

    checkboxChange(event: MatCheckboxChange, rowData: TableData) {
        if (event.checked && !this.selectedMaterials.has(rowData.MaterialId)) {
            this.selectedMaterials.set(rowData.MaterialId, null);
        } else if (!event.checked && this.selectedMaterials.has(rowData.MaterialId)) {
            this.selectedMaterials.delete(rowData.MaterialId);
        }
    }
    isChecked(rowData: TableData) {
        return this.selectedMaterials.has(rowData.MaterialId);
    }
    inputChange($event: number | string, rowData: TableData) {
        console.log({ $event, rowData });
        this.selectedMaterials.set(rowData.MaterialId, isNotNullOrUndefined($event) ? Number($event) : null);
    }
}
